import { ColumnDef } from '@tanstack/react-table'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import LongText from '@/components/long-text'
import { callTypes } from '../data/data'
import { Role, User } from '../data/schema'
import { DataTableRowActions } from './data-table-row-actions'
import i18n from '@/i18n'
import { IconShield, IconUser } from '@tabler/icons-react'
import { DataTableColumnHeader } from '@/components/data-table-column-header'

export const columns: ColumnDef<User>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('name')} />
    ),
    cell: ({ row }) => {
      const { name } = row.original
      return <LongText className='max-w-36'>{name}</LongText>
    },
    meta: {
      className: cn(
        'drop-shadow-[0_1px_2px_rgb(0_0_0_/_0.1)] dark:drop-shadow-[0_1px_2px_rgb(255_255_255_/_0.1)] lg:drop-shadow-none',
        'bg-background transition-colors duration-200 group-hover/row:bg-muted group-data-[state=selected]/row:bg-muted',
        'sticky right md:table-cell'
      ),
    },
    enableHiding: false,
  },
  {
    accessorKey: 'username',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('user')} />
    ),
    cell: ({ row }) => (
      <LongText className='max-w-36'>{row.getValue('username')}</LongText>
    ),
    meta: { className: 'w-36' },
    enableHiding: false,
    enableSorting: true
  },
  {
    id: 'email',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('email')} />
    ),
    cell: ({ row }) => (
      <div className='w-fit text-nowrap'>{row.original.email}</div>
    ),
    enableHiding: false,
    enableSorting: false,
  },
  {
    accessorKey: 'is_active',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('status')} />
    ),
    cell: ({ row }) => {
      const badgeColor = callTypes.get(row.getValue('is_active'))
      return (
        <div className='flex space-x-2'>
          <Badge variant='outline' className={cn('capitalize', badgeColor)}>
            {row.getValue('is_active') ? i18n.t('status_active') : i18n.t('status_locked')}
          </Badge>
        </div>
      )
    },
    enableHiding: false,
    enableSorting: false,
  },
  {
    accessorKey: 'role',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('role')} />
    ),
    cell: ({ row }) => {
      const role: Role = row.original.role
      const Icon = row.original.is_admin
        ? IconShield
        : IconUser
      return (
        <div className='flex w-fit text-nowrap'>
          {role.name}
          <Icon size={16} className='text-muted-foreground' />
        </div>
      );
    },
    enableHiding: false,
    enableSorting: false,
  },
  {
    accessorKey: 'created_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('created_at')} />
    ),
    cell: ({ row }) => (
      <div className='w-fit text-nowrap'>
        {(row.getValue('created_at') as Date).toLocaleDateString('en-GB')}
      </div>
    ),
    enableHiding: false,
    enableSorting: true
  },
  {
    id: 'actions',
    cell: DataTableRowActions,
  },
]
